import {LoadingButton} from '@mui/lab'
import {Alert, IconButton} from '@mui/material'
import Typography from '@mui/material/Typography'
import type {FC} from 'react'
import {useState} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useIsReadOnly} from '@hooks/useIsReadOnly'
import {useSnackbar} from '@hooks/useSnackbar'
import BasicModal from '@shared/components/BasicModal'
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon'

type Props = {
  entityId: string
  entityName: string
  deleteMutation: (id: string) => Promise<unknown>
  refetch?: () => void
  deleteMessageKey: string
  /**
   * Optional last look before deleting: return a reason to refuse, or null to
   * proceed. Shown in the dialog rather than as a toast, because a reason worth
   * blocking on is usually worth reading.
   *
   * Checked on CONFIRM, not on open. Later is more accurate — it closes the
   * window in which the thing that depends on this entity is created while the
   * dialog sits open — and it is the only honest moment when the constraint
   * cannot be enforced by the server, as with a dependency that lives in
   * another service.
   */
  guard?: (id: string) => Promise<string | null>
}

const BasicDeleteButton: FC<Props> = ({
  entityId,
  entityName,
  deleteMutation,
  refetch,
  deleteMessageKey,
  guard,
}) => {
  const [open, setOpen] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [showNotification] = useSnackbar()
  const [t] = useCustomTranslation('common')
  const isReadOnly = useIsReadOnly()
  const handleOpen = (): void => {
    setRefusal(null)
    setOpen(true)
  }
  const handleClose = (): void => setOpen(false)

  const handleConfirmClick = async (): Promise<void> => {
    if (guard) {
      setChecking(true)
      try {
        const reason = await guard(entityId)
        if (reason) {
          setRefusal(reason)
          return
        }
      } finally {
        setChecking(false)
      }
    }
    try {
      await deleteMutation(entityId)
      showNotification({message: t('messages.successful_delete'), severity: 'success'})
      setOpen(false)
      refetch?.()
    } catch (_error) {
      showNotification({message: t('messages.unexpected_error'), severity: 'error'})
    }
  }

  // A read-only platform user (is_staff) may view but never delete.
  if (isReadOnly) {
    return null
  }

  return (
    <>
      <IconButton onClick={handleOpen} data-testid="delete-button" color={'error'}>
        <DeleteOutlineIcon fontSize={'small'} />
      </IconButton>
      <BasicModal
        onClose={handleClose}
        title={t('delete')}
        open={open}
        footerAction={
          <LoadingButton
            variant="contained"
            onClick={handleConfirmClick}
            loading={checking}
            disabled={Boolean(refusal)}
          >
            {t('delete')}
          </LoadingButton>
        }
      >
        {refusal ? (
          <Alert severity="warning" data-cy="delete-refused">
            {refusal}
          </Alert>
        ) : (
          <Typography>{t(deleteMessageKey, {name: entityName})}</Typography>
        )}
      </BasicModal>
    </>
  )
}

export default BasicDeleteButton
